import { Link, useLocation } from "react-router-dom";
import { FACEBOOK_URL, EBAY_URL, getWhatsAppUrl } from "@/lib/contactLinks";
import { FacebookMark, WhatsAppMark, EbayMark } from "./ContactMarks";

export default function Footer() {
  const location = useLocation();
  const waUrl = getWhatsAppUrl(location.pathname);
  return (
    <footer
      className="bg-[#05060A] pt-20 pb-12 border-t border-white/[0.04]"
      data-testid="main-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <img
              src="/logo.png"
              alt="AM Signs & Prints"
              className="h-14 w-auto mb-5"
            />
            <p className="text-brand-text-secondary text-sm max-w-md leading-relaxed" data-testid="footer-company-copy">
              Premium print, signage, and display graphics printed locally in
              Coleraine. Produced on Roland TrueVIS VG-640 with large-format
              capability for canvas, murals, banners, stickers, and branded
              display work.
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5" data-testid="footer-capability-tags">
              {[
                "Printed locally in Coleraine",
                "Roland TrueVIS VG-640",
                "Large-format print capability",
              ].map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-white/[0.08] px-3 py-1.5 text-[10px] tracking-[0.16em] uppercase text-brand-text-secondary/55"
                  data-testid={`footer-capability-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="md:col-span-3 md:col-start-7 self-start">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-brand-gold mb-6">
              Explore
            </h3>
            <div className="space-y-3">
              <Link
                to="/canvas-category-landing"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-canvas-link"
              >
                Canvas Prints
              </Link>
              <Link
                to="/wall-murals-landing"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-murals-link"
              >
                Wall Murals
              </Link>
              <Link
                to="/custom-order-landing"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-custom-link"
              >
                Custom Orders
              </Link>
              <Link
                to="/stickers-labels"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-stickers-link"
              >
                Stickers & Labels
              </Link>
              <Link
                to="/banners"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-banners-link"
              >
                Banners
              </Link>
              <Link
                to="/vehicle-graphics"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-vehicle-graphics-link"
              >
                Vehicle Graphics
              </Link>
            </div>
          </div>

          <div className="md:col-span-3 md:col-start-10 self-start">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-brand-gold mb-6">
              Get in Touch
            </h3>
            <div className="space-y-3">
              <Link
                to="/request-quote"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-quote-link"
              >
                Request a Quote
              </Link>
              <a
                href="tel:07597650510"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-adrian-phone"
              >
                Adrian: 07597 650510
              </a>
              <a
                href="tel:07947439865"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300"
                data-testid="footer-matthew-phone"
              >
                Matthew: 07947 439865
              </a>
              <a
                href="mailto:hello@amdecorsignsandprints.co.uk"
                className="block text-sm text-brand-text-secondary hover:text-brand-text transition-colors duration-300 break-all"
                data-testid="footer-email-link"
              >
                hello@amdecorsignsandprints.co.uk
              </a>
              <p
                className="text-sm text-brand-text-secondary"
                data-testid="footer-location"
              >
                Coleraine, Northern Ireland
              </p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-sm border border-[#214437] bg-[#0B1110] px-4 py-3 text-sm text-brand-text transition-colors duration-300 hover:border-[#2f6a54]"
                data-testid="footer-whatsapp-link"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366]/14 text-[#63e099]">
                  <WhatsAppMark className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.18em] text-brand-text-secondary/45">
                    Primary contact
                  </span>
                  <span className="block text-sm font-medium text-brand-text">
                    Chat on WhatsApp
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-5">
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-brand-text-secondary transition-colors duration-300 hover:text-brand-text"
                  data-testid="footer-facebook-link"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1877F2]/12 text-[#7cb0ff]">
                    <FacebookMark className="h-3.5 w-3.5" />
                  </span>
                  <span>Facebook</span>
                </a>
                <span className="h-4 w-px bg-white/8" />
                <a
                  href={EBAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-brand-text-secondary transition-colors duration-300 hover:text-brand-text"
                  data-testid="footer-ebay-link"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E53238]/10 text-[#f0787c]">
                    <EbayMark className="h-3.5 w-3.5" />
                  </span>
                  <span>eBay</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/[0.04] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-brand-text-secondary/60">
            &copy; {new Date().getFullYear()} AM Decor Signs & Prints. All
            rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy-policy"
              className="text-xs text-brand-text-secondary/60 hover:text-brand-text transition-colors duration-300"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="hidden sm:block h-3 w-px bg-white/8" />
            <p className="hidden sm:block text-xs text-brand-text-secondary/40">
              Printed locally in Coleraine • Roland TrueVIS VG-640 • Large-format print &amp; signage
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
